import type { DiagnosticsEvent, DiagnosticsSeverity } from './ring-buffer.js'

/**
 * Diagnostics sanitizer — §15.5, OPS-04, AT-023.
 * Never export document content, field values, signature data, recipient PII,
 * capability/signing tokens or raw provider messages. Redaction is applied to
 * every event before it enters the ring buffer or an export.
 */

export const REDACTED = '[redacted]'
export const REDACTED_EMAIL = '[redacted-email]'
export const REDACTED_SECRET = '[redacted-secret]'
export const REDACTED_LONG_VALUE = '[redacted-long-value]'
export const REDACTED_CODE = '[redacted-code]'

const TOKEN_QUERY_PATTERN = /([?&](?:token|capability|code|key|secret|sig|signature)=)[^&\s]+/gi
const URL_PATTERN = /https?:\/\/[^\s"']+/gi
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const SECRETISH_PATTERN = /\b[A-Za-z0-9_-]{32,}\b/g
const MIN_TOKEN_LENGTH = 12

const SENSITIVE_QUERY_KEY = /token|capability|code|key|secret|sig|signature/i

function redactUrlValue(raw: string): string {
  try {
    const url = new URL(raw)
    url.pathname = url.pathname
      .split('/')
      .map((segment) => (segment.length >= MIN_TOKEN_LENGTH ? REDACTED_SECRET : segment))
      .join('/')
    for (const key of [...url.searchParams.keys()]) {
      const value = url.searchParams.get(key) ?? ''
      if (SENSITIVE_QUERY_KEY.test(key) || value.length >= MIN_TOKEN_LENGTH) {
        url.searchParams.set(key, REDACTED_SECRET)
      }
    }
    return url.toString()
  } catch {
    return raw
  }
}
const MAX_VALUE_LENGTH = 120

/**
 * Context keys that keep their value: an operational fact is what makes a diagnostics bundle worth
 * having, and these carry no content, credential or identity (they are checked **before** the
 * sensitive test so a name like `message_key` is not mistaken for a message body).
 */
const SAFE_CONTEXT_KEY =
  /^(event_code|message_key|error_code|status_code|http_status|provider_code|provider|route|queue|attempt|count|duration_ms|app_version|platform|locale|state|step)$/i

/**
 * Context keys whose value is never exported: credentials, identity, content and anything that
 * carries what a person typed or what a document says. `body`/`message`/`subject`/`snippet` joined
 * the list after the canary suite showed a whole invitation body surviving under `message_body`.
 */
const SENSITIVE_CONTEXT_KEY =
  /(token|secret|signature|email|phone|recipient|name|document|content|field_value|payload|bytes|pii|body|message|subject|snippet|excerpt|answer|otp|passcode|one_time|verification_code|entered_code|iban|card|pan|account_number|routing_number|cvc|cvv)/i

/**
 * A bare 4–8 digit context value is a one-time code far more often than it is an operational fact,
 * and it is short enough to slip past every other rule: the canary suite caught an OTP exported
 * verbatim under `otp`/`entered_code`. Applied to context *values* only, so a number inside a longer
 * message keeps its meaning.
 */
const CODE_VALUE_PATTERN = /^\d{4,8}$/

export interface SanitizeOptions {
  readonly extra_secrets?: readonly string[]
}

export function sanitizeText(text: string, options: SanitizeOptions = {}): string {
  if (text.length > MAX_VALUE_LENGTH) return REDACTED_LONG_VALUE
  let sanitized = text
  for (const secret of options.extra_secrets ?? []) {
    if (secret.length > 0) sanitized = sanitized.split(secret).join(REDACTED_SECRET)
  }
  // Token-bearing URLs are redacted at every layer (SEC-03): path segments and
  // query values that look like capabilities never survive.
  sanitized = sanitized.replace(URL_PATTERN, (match) => redactUrlValue(match))
  sanitized = sanitized.replace(TOKEN_QUERY_PATTERN, `$1${REDACTED_SECRET}`)
  sanitized = sanitized.replace(EMAIL_PATTERN, REDACTED_EMAIL)
  sanitized = sanitized.replace(SECRETISH_PATTERN, REDACTED_SECRET)
  return sanitized
}

function sanitizeValue(
  value: string | number | boolean | null,
  key: string,
  options: SanitizeOptions,
): string | number | boolean | null {
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return value
  if (SAFE_CONTEXT_KEY.test(key)) return sanitizeText(value, options)
  if (SENSITIVE_CONTEXT_KEY.test(key)) return REDACTED
  if (CODE_VALUE_PATTERN.test(value)) return REDACTED_CODE
  return sanitizeText(value, options)
}

export interface DiagnosticsEventInput {
  readonly timestamp: string
  readonly event_code: string
  readonly severity: DiagnosticsSeverity
  readonly context?: Readonly<Record<string, string | number | boolean | null>>
}

export function sanitizeDiagnosticsEvent(
  input: DiagnosticsEventInput,
  options: SanitizeOptions = {},
): DiagnosticsEvent {
  const context: Record<string, string | number | boolean | null> = {}
  for (const [key, value] of Object.entries(input.context ?? {})) {
    context[key] = sanitizeValue(value, key, options)
  }
  return {
    timestamp: input.timestamp,
    event_code: sanitizeText(input.event_code, options),
    severity: input.severity,
    context,
  }
}

/** Redact a token-bearing URL for display or logging (SEC-03). */
export function redactUrl(url: string, options: SanitizeOptions = {}): string {
  return sanitizeText(url, options)
}
